import React, { useEffect, useState } from "react";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, BarChart, Bar,
} from "recharts";
import { Loader2, TrendingUp, Mail, MailOpen, PhoneCall, FileText, Edit3, Calendar, ExternalLink, BarChart3 } from "lucide-react";
import { adminStats, formatApiError } from "../lib/api";

// Constants for chart colors (studio palette)
const GOLD = "#D4AF37";
const NAVY = "#0B132B";

const StatCard = ({ label, value, sub, icon: Icon, testId, highlight }) => (
    <div
        data-testid={testId}
        className={`bg-white p-6 ${highlight ? "ring-2 ring-gold" : "border border-slate-200"}`}
    >
        <div className="flex items-center justify-between mb-2">
            <span className="label-eyebrow">{label}</span>
            {Icon && <Icon size={16} className="text-gold-deep" />}
        </div>
        <div className="font-serif text-3xl text-navy">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
);

const formatDay = (dateStr) => {
    // "2026-09-22" -> "22/09"
    if (!dateStr || dateStr.length < 10) return dateStr;
    return `${dateStr.slice(8, 10)}/${dateStr.slice(5, 7)}`;
};

export default function AdminStats() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const res = await adminStats();
                if (mounted) setData(res);
            } catch (err) {
                if (mounted) setError(formatApiError(err));
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    if (loading) {
        return (
            <div className="py-20 flex items-center justify-center text-slate-500" data-testid="stats-loading">
                <Loader2 size={20} className="animate-spin mr-3" /> Caricamento statistiche...
            </div>
        );
    }
    if (error) {
        return (
            <div className="p-6 border border-red-500 bg-red-50 text-red-800 text-sm" data-testid="stats-error">{error}</div>
        );
    }
    if (!data) return null;

    const { contacts, articles } = data;
    const dailyChart = contacts.daily.map((d) => ({ ...d, day: formatDay(d.date) }));
    const oggettiChart = contacts.top_oggetti.map((o) => ({
        ...o,
        // truncate oggetto for display
        oggetto_short: o.oggetto && o.oggetto.length > 24 ? `${o.oggetto.slice(0, 22)}…` : (o.oggetto || "—"),
    }));

    const avg7d = contacts.last_7d > 0 ? (contacts.last_7d / 7).toFixed(1) : "0";
    const richiamiRate = contacts.last_30d > 0
        ? `${Math.round((contacts.richiami_30d / contacts.last_30d) * 100)}%`
        : "—";

    return (
        <div className="space-y-10" data-testid="admin-stats">
            {/* CONTATTI */}
            <section>
                <div className="flex items-center gap-3 mb-6">
                    <BarChart3 size={18} className="text-gold-deep" />
                    <h2 className="font-serif text-navy text-2xl">Contatti dal sito</h2>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 mb-8">
                    <StatCard
                        label="Oggi"
                        value={contacts.today}
                        icon={Calendar}
                        testId="stat-contacts-today"
                    />
                    <StatCard
                        label="Ultimi 7 giorni"
                        value={contacts.last_7d}
                        sub={`~${avg7d} al giorno`}
                        icon={TrendingUp}
                        testId="stat-contacts-7d"
                    />
                    <StatCard
                        label="Ultimi 30 giorni"
                        value={contacts.last_30d}
                        icon={Mail}
                        testId="stat-contacts-30d"
                    />
                    <StatCard
                        label="Non letti"
                        value={contacts.unread}
                        sub={`su ${contacts.total} totali`}
                        icon={MailOpen}
                        testId="stat-contacts-unread"
                        highlight={contacts.unread > 0}
                    />
                </div>

                {/* Trend chart 30gg */}
                <div className="bg-white border border-slate-200 p-6 mb-8" data-testid="stat-daily-chart">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-serif text-navy text-lg">Andamento — ultimi 30 giorni</h3>
                        <div className="text-xs text-slate-500">
                            <PhoneCall size={12} className="inline mr-1 text-gold-deep" />
                            {contacts.richiami_30d} richiami richiesti ({richiamiRate})
                        </div>
                    </div>
                    <div style={{ width: "100%", height: 240 }}>
                        <ResponsiveContainer>
                            <LineChart data={dailyChart} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="day" stroke="#64748b" fontSize={11} tick={{ fontSize: 11 }} interval={3} />
                                <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                                <Tooltip
                                    contentStyle={{ background: NAVY, border: `1px solid ${GOLD}`, color: "#fff", fontSize: 12 }}
                                    labelStyle={{ color: GOLD }}
                                    formatter={(v) => [`${v} contatti`, ""]}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="count"
                                    stroke={GOLD}
                                    strokeWidth={2}
                                    dot={{ fill: GOLD, r: 3 }}
                                    activeDot={{ r: 5, fill: NAVY, stroke: GOLD, strokeWidth: 2 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Top oggetti */}
                {oggettiChart.length > 0 && (
                    <div className="bg-white border border-slate-200 p-6" data-testid="stat-top-oggetti">
                        <h3 className="font-serif text-navy text-lg mb-4">
                            Argomenti più richiesti <span className="text-slate-400 text-sm font-sans">(ultimi 90gg)</span>
                        </h3>
                        <div style={{ width: "100%", height: Math.max(160, oggettiChart.length * 40) }}>
                            <ResponsiveContainer>
                                <BarChart data={oggettiChart} layout="vertical" margin={{ top: 0, right: 30, left: 10, bottom: 0 }}>
                                    <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis type="number" stroke="#64748b" fontSize={11} allowDecimals={false} />
                                    <YAxis type="category" dataKey="oggetto_short" stroke="#64748b" fontSize={11} width={170} />
                                    <Tooltip
                                        contentStyle={{ background: NAVY, border: `1px solid ${GOLD}`, color: "#fff", fontSize: 12 }}
                                        labelStyle={{ color: GOLD }}
                                        formatter={(v) => [`${v} richieste`, ""]}
                                    />
                                    <Bar dataKey="count" fill={GOLD} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
            </section>

            {/* ARTICOLI */}
            <section>
                <div className="flex items-center gap-3 mb-6">
                    <FileText size={18} className="text-gold-deep" />
                    <h2 className="font-serif text-navy text-2xl">Articoli del blog</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200">
                    <StatCard
                        label="Pubblicati"
                        value={articles.published}
                        icon={FileText}
                        testId="stat-articles-published"
                    />
                    <StatCard
                        label="Pubblicati ultimi 30gg"
                        value={articles.published_30d}
                        icon={TrendingUp}
                        testId="stat-articles-published-30d"
                    />
                    <StatCard
                        label="Bozze in attesa"
                        value={articles.drafts}
                        icon={Edit3}
                        testId="stat-articles-drafts"
                        highlight={articles.drafts > 0}
                    />
                </div>
            </section>

            {/* Traffico esterno */}
            <section>
                <div className="flex items-center gap-3 mb-6">
                    <TrendingUp size={18} className="text-gold-deep" />
                    <h2 className="font-serif text-navy text-2xl">Traffico e SEO</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <a
                        href="https://analytics.google.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="ext-ga4-link"
                        className="bg-white border border-slate-200 p-6 hover:border-gold transition-colors group"
                    >
                        <div className="flex items-start justify-between mb-3">
                            <span className="label-eyebrow">Google Analytics 4</span>
                            <ExternalLink size={14} className="text-slate-400 group-hover:text-gold-deep transition-colors" />
                        </div>
                        <div className="font-serif text-navy text-xl mb-2">Visitatori & sessioni</div>
                        <p className="text-sm text-slate-500 leading-relaxed">
                            Traffico in tempo reale, sorgenti, pagine più viste, geolocalizzazione, conversioni.
                        </p>
                    </a>
                    <a
                        href="https://search.google.com/search-console"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="ext-gsc-link"
                        className="bg-white border border-slate-200 p-6 hover:border-gold transition-colors group"
                    >
                        <div className="flex items-start justify-between mb-3">
                            <span className="label-eyebrow">Search Console</span>
                            <ExternalLink size={14} className="text-slate-400 group-hover:text-gold-deep transition-colors" />
                        </div>
                        <div className="font-serif text-navy text-xl mb-2">Posizionamento Google</div>
                        <p className="text-sm text-slate-500 leading-relaxed">
                            Keyword, click, impression, posizionamento medio, richieste di indicizzazione.
                        </p>
                    </a>
                </div>
            </section>
        </div>
    );
}
